import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ProductSelectionModal from './ProductSelectionModal';
import axios from 'axios';
import { fetchSettings } from '../settings/controller/settings-controller';

const OrderItemsTable = ({
  items = [], // Add default empty array
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  totals = {}, // Add default empty object
  statusSelector = null,
  onUpdateTotals = () => { }
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Update the useEffect to only run on component mount, not when totals change
  useEffect(() => {
    const getSettings = async () => {
      try {
        const settingsData = await fetchSettings();
        if (settingsData) {
          setSettings(settingsData);
          
          // Only set delivery fee if it hasn't been initialized yet
          if (totals.deliveryFee === undefined) {
            // Keep the delivery fee as 0 if it's already set to 0 in totals
            const currentDeliveryFee = parseFloat(settingsData.fraisDeLivraison || 0);
            
            // Update delivery fee in totals
            onUpdateTotals({
              ...totals,
              deliveryFee: currentDeliveryFee
            });
          }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    getSettings();
    // Empty dependency array to ensure it only runs once on component mount
  }, []);

  // Safe number formatting function
  const safeFormat = (value) => {
    if (value === undefined || value === null) {
      return "0.00";
    }

    const num = typeof value === 'string' ? parseFloat(value) : value;

    if (typeof num !== 'number' || isNaN(num)) {
      return "0.00";
    }

    try {
      return num.toFixed(2);
    } catch (e) {
      console.error("Error formatting number:", e);
      return "0.00";
    }
  };

  // Update the calculateItemTotal function to use the formula: price / (1 + TVA/100)
  const calculateItemTotal = (item) => {
    // Calculate the HT price using the formula
    const unitHtPrice = item.price / (1 + item.tva / 100);

    // Calculate total HT considering quantity
    return unitHtPrice * item.quantity;
  };

  // Function to calculate TTC from HT and TVA rate
  const calculateTTC = (ht, tvaRate) => {
    return ht * (1 + tvaRate / 100);
  };

  // Add similar safe checks to any calculations
  const calculateTotalHT = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      // First apply discount
      const priceAfterDiscount = item.price * item.quantity * (1 - item.remise / 100);

      // Then remove TVA to get HT
      const itemHT = priceAfterDiscount * (1 - item.tva / 100);
      return sum + itemHT;
    }, 0);
  };

  // Default totals if not provided
  const defaultTotals = {
    subtotal: 0,
    tva: 0,
    total: 0,
    remise: 0,
    deliveryFee: settings?.fraisDeLivraison || 0
  };

  // Merge provided totals with defaults
  const finalTotals = { ...defaultTotals, ...totals };

  // Calculate total without delivery fee for HT calculation
  const totalBeforeDelivery = finalTotals.total - (finalTotals.deliveryFee || 0);

  // Calculate the correct total HT (without TVA) using our formula
  const totalHT = calculateTotalHT(items || []);

  // Handle product selection from modal
  const handleProductSelect = (selectedProducts) => {
    if (!selectedProducts || !Array.isArray(selectedProducts) || selectedProducts.length === 0) {
      console.warn("No products selected or invalid selection format");
      return;
    }

    console.log("Selected products:", selectedProducts);

    // Helper: parse number with comma or dot
    const parseFlexibleNumber = (v) => {
      if (v === null || v === undefined) return 0;
      if (typeof v === 'number') return v;
      const s = String(v).trim().replace(',', '.');
      const n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    };

    // Helper: interpret flexible truthy flags
    const isTruthyFlag = (val) => {
      if (val === true || val === 1) return true;
      if (typeof val === 'string') {
        const l = val.toLowerCase().trim();
        return l === 'true' || l === '1' || l === 'yes' || l === 'y';
      }
      return false;
    };

    // Check if onAddItem is a function
    if (typeof onAddItem !== 'function') {
      console.error("onAddItem is not a function:", onAddItem);
      return;
    }

    // Process one product at a time to avoid state batching issues
    const processOneByOne = () => {
      // Create a copy to avoid mutation during processing
      const productsToBatch = [...selectedProducts];

      // Function to process a single product
      const processProduct = (index) => {
        if (index >= productsToBatch.length) return;

        const selectedProduct = productsToBatch[index];
        console.log(`Processing product ${index + 1}/${productsToBatch.length}: ${selectedProduct.name}`);

        // For debugging, check that items is an array
        console.log("Current items:", items);

        // Check if this product already exists in the order
        const existingItemIndex = items && items.length > 0 ?
          items.findIndex(item => {
            const isVariantMatch = selectedProduct.isVariant && item.variantId === selectedProduct.id;
            const isProductMatch = !selectedProduct.isVariant && item.productId === selectedProduct.id && !item.variantId;
            return isVariantMatch || isProductMatch;
          }) : -1;

        if (existingItemIndex >= 0) {
          // Update the quantity if product already exists
          const existingItem = items[existingItemIndex];
          console.log(`Updating quantity for existing item: ${existingItem.name}`);

          onUpdateItem(existingItem.id, {
            ...existingItem,
            quantity: existingItem.quantity + selectedProduct.quantity
          });

          // Process the next product after a small delay
          setTimeout(() => processProduct(index + 1), 100);
        } else {
          // Add as new item if it doesn't exist
          console.log(`Adding new item: ${selectedProduct.name}`);

          // Determine promo-aware price
          const promoFlag = isTruthyFlag(selectedProduct.enPromo) || isTruthyFlag(selectedProduct.EnPromo) || isTruthyFlag(selectedProduct.product?.enPromo) || isTruthyFlag(selectedProduct.product?.EnPromo) || isTruthyFlag(selectedProduct.variant?.enPromo);
          const promoCandidates = [
            selectedProduct.prixVentePromo,
            selectedProduct.PrixVentePromo,
            selectedProduct.prixPromo,
            selectedProduct.product?.prixVentePromo,
            selectedProduct.variant?.prixVentePromo,
            selectedProduct.product?.prixVenteTTC,
            selectedProduct.price
          ];

          let extractedPrice = 0;
          if (promoFlag) {
            for (const c of promoCandidates) {
              const n = parseFlexibleNumber(c);
              if (n > 0) {
                extractedPrice = n;
                break;
              }
            }
          }

          // Fallbacks
          if (!extractedPrice) {
            extractedPrice = parseFlexibleNumber(selectedProduct.price) || parseFlexibleNumber(selectedProduct.prixVenteTTC) || parseFlexibleNumber(selectedProduct.product?.prixVenteTTC) || 0;
          }

          const newItem = {
            id: uuidv4(),
            productId: selectedProduct.productId,
            variantId: selectedProduct.isVariant ? selectedProduct.id : null,
            name: selectedProduct.name,
            reference: selectedProduct.reference || 'Sans référence',
            price: extractedPrice,
            quantity: selectedProduct.quantity || 1,
            tva: 19, // Default TVA rate
            remise: 0, // Default discount
            stock: selectedProduct.stock || 999,
            isVariant: selectedProduct.isVariant || false
          };

          // Add the item and verify it worked
          onAddItem(newItem);
          console.log("Added new item, checking if onAddItem worked:", newItem);

          // Process the next product after a small delay
          setTimeout(() => processProduct(index + 1), 100);
        }
      };

      // Start processing with the first product
      processProduct(0);
    };

    // Begin processing
    processOneByOne();

    // Close the modal after processing
    setIsModalOpen(false);
  };

  return (
    <div className="grid grid-cols-1 gap-6">
      {/* Order Items Table */}
      <div className="lg:col-span-2">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          {/* Header Section */}
          <div className="px-6 py-5 sm:px-8 bg-white border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="p-2 rounded-md bg-[#f5f8f7] mr-4">
                  <svg className="h-6 w-6 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Articles de la commande</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Détails des produits et services associés à cette commande
                  </p>
                </div>
              </div>

              {/* Update the delivery fee section with both free delivery and reset buttons */}
              {settings?.fraisDeLivraison !== undefined && (
                <div className="flex items-center space-x-2">
                  {/* Only show the "Free Delivery" button when delivery fee is NOT already 0 */}
                  {parseFloat(totals.deliveryFee) > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        // Set delivery fee to 0 and update totals
                        const updatedTotals = {
                          ...totals,
                          deliveryFee: 0
                        };

                        // Update the parent component's totals
                        onUpdateTotals(updatedTotals);

                        // Recalculate the total TTC with free delivery
                        if (typeof onUpdateTotals === 'function') {
                          // Make sure to adjust the total amount properly when removing delivery fee
                          if (totals.total && totals.deliveryFee > 0) {
                            updatedTotals.total = totals.total - totals.deliveryFee;
                          }

                          // Call update totals with the adjusted values
                          onUpdateTotals(updatedTotals);
                        }
                      }}
                      className="text-xs bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 font-medium rounded-md px-2 py-1 transition-colors duration-200"
                    >
                      Livraison gratuite
                    </button>
                  )}

                  {/* Add a reset button to restore the original delivery fee */}
                  {parseFloat(totals.deliveryFee) === 0 && parseFloat(settings?.fraisDeLivraison) > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        // Reset delivery fee to the original value from settings
                        const originalFee = parseFloat(settings.fraisDeLivraison);
                        
                        // Create a deep copy of totals to avoid reference issues
                        const updatedTotals = JSON.parse(JSON.stringify(totals));
                        
                        // Explicitly update the delivery fee
                        updatedTotals.deliveryFee = originalFee;
                        
                        // Make sure to adjust the total amount properly when adding back delivery fee
                        if (updatedTotals.total !== undefined) {
                          updatedTotals.total = parseFloat(updatedTotals.total || 0) + originalFee;
                        }
                        
                        // Log for debugging
                        console.log("Reset delivery fee:", originalFee);
                        console.log("Updated totals object:", updatedTotals);
                        
                        // Add a slight delay before updating to ensure state updates correctly
                        setTimeout(() => {
                          onUpdateTotals(updatedTotals);
                          
                          // Force the api request to see the updated value (add this if needed)
                          if (window.lastOrderTotals) {
                            window.lastOrderTotals.fraisDeLivraison = originalFee;
                          }
                        }, 10);
                      }}
                      className="text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 font-medium rounded-md px-2 py-1 transition-colors duration-200"
                    >
                      Réinitialiser
                    </button>
                  )}

                  <div className="text-sm bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5">
                    <span className="font-medium text-gray-700">Frais de livraison:</span>{' '}
                    <span className={`font-semibold ${parseFloat(totals.deliveryFee) === 0 ? 'text-green-600' : 'text-[#174540]'}`}>
                      {parseFloat(totals.deliveryFee) === 0
                        ? 'Gratuit'
                        : `${safeFormat(totals.deliveryFee)} TND`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Show empty state if no items */}
          {(!items || items.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-14 px-4 sm:px-6 border-t border-gray-200 bg-gray-50">
              <div className="p-3 rounded-md bg-[#f5f8f7] mb-4">
                <svg className="h-12 w-12 text-[#174540]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="mt-2 text-lg font-medium text-gray-900">Aucun article dans la commande</h3>
              <p className="mt-2 text-center text-sm text-gray-500 max-w-md">
                Ajoutez des produits ou services à cette commande pour les voir apparaître ici.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] transition-all duration-200"
                >
                  <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Ajouter un nouvel article
                </button>
              </div>
            </div>
          ) : (
            /* Display items table if there are items */
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produit
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Référence
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix unitaire
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantité
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      TVA (%)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Remise (%)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total HT
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => {
                    // This calculates the Total HT (price without TVA, with discount applied)
                    const itemTotalHT = calculateItemTotal(item);

                    return (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.reference}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.price.toFixed(2)} TND
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <button
                              type="button"
                              className="text-gray-400 hover:text-gray-600 focus:outline-none"
                              onClick={() => onUpdateItem(item.id, { ...item, quantity: Math.max(1, item.quantity - 1) })}
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            </button>
                            <input
                              type="number"
                              className="mx-2 w-14 text-center border-gray-300 rounded-md text-sm"
                              value={item.quantity}
                              onChange={(e) => onUpdateItem(item.id, { ...item, quantity: parseInt(e.target.value) || 1 })}
                              min="1"
                              max={item.stock || 999}
                            />
                            <button
                              type="button"
                              className="text-gray-400 hover:text-gray-600 focus:outline-none"
                              onClick={() => onUpdateItem(item.id, {
                                ...item,
                                quantity: Math.min(item.stock || 999, item.quantity + 1)
                              })}
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <input
                            type="number"
                            className="w-14 text-center border-gray-300 rounded-md text-sm"
                            value={item.tva}
                            onChange={(e) => onUpdateItem(item.id, { ...item, tva: parseFloat(e.target.value) || 0 })}
                            min="0"
                            max="100"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <input
                            type="number"
                            className="w-14 text-center border-gray-300 rounded-md text-sm"
                            value={item.remise}
                            onChange={(e) => onUpdateItem(item.id, { ...item, remise: parseFloat(e.target.value) || 0 })}
                            min="0"
                            max="100"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {safeFormat(itemTotalHT)} TND
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            className="text-red-600 hover:text-red-900 focus:outline-none"
                            onClick={() => onRemoveItem(item.id)}
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary footer - only show if there are items */}
          {items && items.length > 0 && (
            <div className="border-t border-gray-200 px-6 py-4 sm:px-8 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <div className="text-sm text-gray-500 mb-3 sm:mb-0">
                  {items.length} article{items.length > 1 ? 's' : ''} dans la commande
                </div>
                <div className="flex items-center">
                  {/* Display corrected total HT */}
                  <div className="mr-6 text-sm">
                    <span className="font-medium">Total HT:</span> {safeFormat(totalHT)} TND
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                  >
                    <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Ajouter plus d'articles
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProductSelect={handleProductSelect}
      />
    </div>
  );
};

export default OrderItemsTable;